/**
 * HIPAA-Compliant Protected Route Component
 * 
 * Features:
 * - Authentication guard (redirects to login if not authenticated)
 * - Role-based authorization (checks user role)
 * - Loading states during auth checks
 * - Logs unauthorized access attempts
 * - Redirects with return URL (for better UX)
 * - Dedicated unauthorized page
 * 
 * Based on HIPAA_COMPLIANCE_COMPLETE_SECURITY_GUIDE.md
 * Frontend guide lines 668-718
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import authService from '../services/authService';

/**
 * Protected Route Component
 * 
 * @param {ReactNode} children - Component to render if authorized
 * @param {array} requiredRoles - Array of required roles (e.g., ['admin', 'super_admin'])
 * @param {boolean} requireAdmin - Legacy prop: Requires admin role (converts to requiredRoles)
 * @param {ReactNode} loadingComponent - Component to show while loading (optional)
 */
const ProtectedRoute = ({ 
  children, 
  requiredRoles = [], 
  requireAdmin = false,
  loadingComponent = null 
}) => {
  const { isAuthenticated, user, isLoading, isInitialized } = useAuth();
  const location = useLocation();
  
  // Handle legacy requireAdmin prop (backward compatibility)
  const effectiveRequiredRoles = requireAdmin 
    ? ['admin', 'super_admin'] 
    : requiredRoles;
  
  // Show loading state while initializing auth
  if (!isInitialized || isLoading) {
    if (loadingComponent) {
      return loadingComponent;
    }
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }
  
  // Check authentication
  if (!isAuthenticated) {
    // Save intended destination for redirect after login
    const redirect = encodeURIComponent(location.pathname + location.search);
    console.log('User not authenticated, redirecting to login');
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }
  
  // Check authorization (role-based)
  if (effectiveRequiredRoles.length > 0) {
    if (!effectiveRequiredRoles.includes(user.role)) {
      // Log unauthorized access attempt
      console.error('HIPAA: Unauthorized access attempt:', {
        user: user.id,
        email: user.email,
        requiredRoles: effectiveRequiredRoles,
        actualRole: user.role,
        path: location.pathname,
        timestamp: new Date().toISOString()
      });
      
      // Send audit log to backend (don't await - fire and forget)
      authService.post('/audit/unauthorized-access', {
        path: location.pathname,
        requiredRoles: effectiveRequiredRoles,
        actualRole: user.role
      }).catch(err => {
        console.error('Failed to log unauthorized access to backend:', err);
      });
      
      return <Navigate to="/unauthorized" replace />;
    }
  }
  
  // Authorized - render children
  return children;
};

export default ProtectedRoute;
